import { Project } from '../../services/projectService';

export interface SocialAccount {
  id: string;
  platform: 'youtube' | 'instagram' | 'tiktok';
  platformUsername: string;
  displayName?: string;
  profileImageUrl?: string;
  accountNickname?: string;
  isDefault?: boolean;
  isActive: boolean;
  expiresAt: string;
  isExpired: boolean;
  needsReconnection: boolean;
  timeUntilExpiry: number | null;
  status: 'expired' | 'active';
  createdAt: string;
  updatedAt: string;
  accountMetadata?: {
    subscriberCount?: number;
    channelId?: string;
    followerCount?: number;
    videoCount?: number;
  };
}

// Published post status for UI
export enum PublishState {
  DRAFT = 'draft', // Not yet scheduled
  SCHEDULED = 'scheduled', // Scheduled for future
  PUBLISHING = 'publishing', // Currently being uploaded
  LIVE_PUBLIC = 'live_public', // Published and public
  LIVE_UNLISTED = 'live_unlisted', // Published but unlisted
  LIVE_PRIVATE = 'live_private', // Published but private
  FAILED = 'failed', // Failed to publish
  CANCELLED = 'cancelled', // User cancelled
}

// Social media post from backend
export interface SocialPost {
  id: string;
  projectId: string;
  socialAccountId: string;
  platform: SocialPlatform;
  title: string;
  description: string;
  thumbnailUrl?: string;
  privacy: PrivacyLevel;
  scheduledAt?: string;
  status: 'scheduled' | 'publishing' | 'published' | 'failed' | 'cancelled';
  platformPostId?: string;
  platformUrl?: string;
  platformData?: Record<string, any>;
  errorMessage?: string;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  // Computed field from backend
  publishState: PublishState;
  // Relations
  project?: ProjectWithVideos;
  socialAccount?: SocialAccount;
}

export interface ProjectWithVideos extends Project {
  originalVideoUrl?: string;
  captionedVideoUrl?: string;
  generatedVideoUrl?: string;
  soundTrackVideoUrl?: string;
  // Project already has: id, title, type, duration, thumbnail, date, etc.
}

export interface VideoSelection {
  projectId: string;
  videoUrl: string;
  useCaption: boolean;
  hasOriginal: boolean;
  hasCaptioned: boolean;
}

export interface ShareFormData {
  projectId: string;
  socialAccountId: string;
  platform: 'youtube' | 'instagram' | 'tiktok';
  title: string;
  description: string;
  thumbnailUrl?: string;
  privacy: 'public' | 'unlisted' | 'private';
  scheduledAt?: string;
  scheduleForLater: boolean;
  tags?: string[];
  category?: string;
}

export interface AuthUrlResponse {
  url: string;
  state: string;
}

export interface ProjectData {
  id: string;
  title: string;
  description?: string;
  videoUrl: string;
  thumbnailUrl?: string;
  // Generated title data
  titleData?: {
    title: string;
    selectedTitle?: string;
    description: string;
    generatedDescription?: string;
    tags: string[];
    timeline?: Array<{ time: string; description: string; }>;
    hashtags?: string[];
  };
  // Additional suggested content
  suggestedContent?: {
    title: string;
    description: string;
    timeline?: Array<{ time: string; description: string; }>;
    hashtags?: string[];
  };
}

export type SocialPlatform = 'youtube' | 'instagram' | 'tiktok';
export type PrivacyLevel = 'public' | 'unlisted' | 'private';
export type CircleSize = 'small' | 'medium' | 'large';
export type AccountDisplayMode = 'management' | 'selection';

export interface GroupedAccounts {
  youtube: SocialAccount[];
  instagram: SocialAccount[];
  tiktok: SocialAccount[];
}

export interface PlatformInfo {
  key: SocialPlatform;
  name: string;
  icon: any;
  color: string;
}

export interface SelectedAccounts {
  youtube?: string;
  instagram?: string;
  tiktok?: string;
}
